import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

export type QueryValue = string | string[] | undefined;
export type QueryParams = Record<string, QueryValue>;

export interface NavigationAdapter {
    query?: QueryParams;
    pathname?: string;
    push?: (url: string) => void | Promise<void>;
    replace?: (url: string) => void | Promise<void>;
}

export interface NavigationContextValue extends Required<Pick<NavigationAdapter, 'push' | 'replace'>> {
    query: QueryParams;
    pathname?: string;
}

const NavigationContext = createContext<NavigationAdapter | undefined>(undefined);

function readWindowQuery(): QueryParams {
    if (typeof window === 'undefined') {
        return {};
    }

    const query: QueryParams = {};
    const params = new URLSearchParams(window.location.search);

    params.forEach((value, key) => {
        const existing = query[key];
        if (existing === undefined) {
            query[key] = value;
        } else if (Array.isArray(existing)) {
            query[key] = [...existing, value];
        } else {
            query[key] = [existing, value];
        }
    });

    return query;
}

function defaultPush(url: string): void {
    if (typeof window !== 'undefined') {
        window.location.assign(url);
    }
}

function defaultReplace(url: string): void {
    if (typeof window !== 'undefined') {
        window.location.replace(url);
    }
}

export interface NavigationProviderProps {
    children: ReactNode;
    adapter?: NavigationAdapter;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children, adapter }) => {
    return <NavigationContext.Provider value={adapter}>{children}</NavigationContext.Provider>;
};

export function useNavigation(): NavigationContextValue {
    const adapter = useContext(NavigationContext);
    const [windowQuery, setWindowQuery] = useState<QueryParams>(() => readWindowQuery());
    const [windowPathname, setWindowPathname] = useState<string | undefined>(() => {
        if (typeof window === 'undefined') return undefined;
        return window.location.pathname;
    });

    useEffect(() => {
        if (adapter?.query) {
            return;
        }

        const updateFromWindow = () => {
            setWindowQuery(readWindowQuery());
            setWindowPathname(window.location.pathname);
        };

        window.addEventListener('popstate', updateFromWindow);
        window.addEventListener('hashchange', updateFromWindow);

        return () => {
            window.removeEventListener('popstate', updateFromWindow);
            window.removeEventListener('hashchange', updateFromWindow);
        };
    }, [adapter?.query]);

    return useMemo(
        () => ({
            query: adapter?.query || windowQuery,
            pathname: adapter?.pathname || windowPathname,
            push: adapter?.push || defaultPush,
            replace: adapter?.replace || defaultReplace
        }),
        [adapter, windowQuery, windowPathname]
    );
}
